import { DataTableQueryContextProvider } from "@/components/data-table/DataTableQueryContext";
import useQueryCreatorForAPIService from "@/components/data-table/hooks/useQueryCreatorForAPIService";
import { QueryTable } from "@/components/query-table/QueryTable";
import { Collapsible, CollapsibleContent } from "@/components/ui/collapsible";
import { DEFAULT_DATE_FORMAT, Module, PATH_SHIFT_REPORT } from "@/constants";
import { Filters, FilterType } from "@/lib/filters";
import { cn } from "@/lib/utils";
import { formatDate } from "@/lib/utils/formatters";
import ShiftReportCollapsibleContent from "@/modules/shiftReports/components/ShiftReportCollapsibleContent";
import ShiftReportExpandCell from "@/modules/shiftReports/components/ShiftReportExpandCell";
import { ShiftReportsService } from "@/modules/shiftReports/service";
import { readShiftReport } from "@/schemas/shiftReport";
import { ReadShiftReport } from "@/types/shiftReport";
import { ColumnDef } from "@tanstack/react-table";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { generatePath } from "react-router-dom";

const defaultFilters: Filters<ReadShiftReport> = [
  {
    id: "shift",
    value: {
      filter: FilterType.IncludesString,
      value: formatDate(new Date(), DEFAULT_DATE_FORMAT),
    },
  },
];

const ShiftReportDirectory = () => {
  const name = "shiftReports";
  const [collapsibleData, setCollapsibleData] =
    useState<ReadShiftReport | null>(null);
  const onCollapsibleClose = useCallback(() => setCollapsibleData(null), []);
  const tableRef = useRef<null | HTMLDivElement>(null);
  const columnOverrides = useMemo<
    Record<string, ColumnDef<ReadShiftReport, number>>
  >(
    () => ({
      department_entries: {
        id: "department_entries",
        enableHiding: false,
        enablePinning: false,
        enableSorting: false,
        enableColumnFilter: false,
        cell: (data) => {
          const rowId = data.row.original.id;
          const isActive = collapsibleData?.id === rowId;
          return (
            <ShiftReportExpandCell
              onClick={setCollapsibleData}
              data={data.row.original}
              isActive={isActive}
            />
          );
        },
      },
    }),
    [collapsibleData?.id],
  );

  useEffect(() => {
    const element = tableRef.current;
    if (element && collapsibleData) {
      element.scrollTo({
        left: element.scrollWidth,
        top: element.scrollTop, // Maintains current vertical position
        behavior: "smooth",
      });
    }
  }, [collapsibleData]);

  const isCollapsibleOpen = Boolean(collapsibleData);

  const { query, options } = useQueryCreatorForAPIService<ReadShiftReport>(
    name,
    ShiftReportsService,
    defaultFilters,
  );

  const queryValue = useMemo(() => ({ query }), [query]);

  return (
    <QueryTable<ReadShiftReport>
      schema={readShiftReport}
      name={name}
      pathBuilder={(row) =>
        generatePath(PATH_SHIFT_REPORT, {
          shiftReportId: row.id,
        })
      }
      query={query}
      options={options}
      columnOverrides={columnOverrides}
      tableFeatures={{
        exportSettings: { moduleName: Module.ShiftReports },
        initialColumnVisibility: {
          shift_date: false,
          shift_name: false,
        },
      }}
    >
      <QueryTable.Header breadcrumbLabel={`${name}.title.multiple`} />

      <QueryTable.Menubar>
        <QueryTable.Menubar.Tools />
        <QueryTable.Menubar.Columns />
        <QueryTable.Menubar.Settings />
      </QueryTable.Menubar>

      <Collapsible open={isCollapsibleOpen} className="flex flex-row gap-2">
        <DataTableQueryContextProvider value={queryValue}>
          <QueryTable.Content
            className={cn({
              "w-3/4": isCollapsibleOpen,
              "w-full": !isCollapsibleOpen,
            })}
          >
            <QueryTable.Status />
          </QueryTable.Content>
        </DataTableQueryContextProvider>

        <CollapsibleContent
          className={cn({
            "w-1/4": isCollapsibleOpen,
          })}
        >
          {collapsibleData && (
            <ShiftReportCollapsibleContent
              data={collapsibleData}
              onClose={onCollapsibleClose}
            />
          )}
        </CollapsibleContent>
      </Collapsible>
    </QueryTable>
  );
};

export default ShiftReportDirectory;
